import React from 'react';
import { AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Risk } from '../../types/risk';
import { formatDate } from '../../utils/formatters';

interface RiskListProps {
  risks: Risk[];
  onRiskUpdate: (riskId: string, updates: Partial<Risk>) => void;
  onRiskDelete: (riskId: string) => void;
}

export default function RiskList({ risks, onRiskUpdate, onRiskDelete }: RiskListProps) {
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'mitigated':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'monitoring':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
    }
  };

  return (
    <div className="divide-y divide-gray-200">
      {risks.map((risk) => (
        <div key={risk.id} className="p-4 hover:bg-gray-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              {getStatusIcon(risk.status)}
              <div>
                <h4 className="text-sm font-medium text-gray-900">{risk.title}</h4>
                <p className="mt-1 text-sm text-gray-500">{risk.description}</p>
                <div className="mt-2 flex items-center space-x-4">
                  <span className="text-xs text-gray-500">
                    Updated: {formatDate(risk.updatedAt)}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    risk.impact === 'high'
                      ? 'bg-red-100 text-red-800'
                      : risk.impact === 'medium'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {risk.impact.toUpperCase()}
                  </span>
                  <span className="text-xs font-medium text-gray-500 capitalize">
                    {risk.category}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onRiskUpdate(risk.id, {
                  status: risk.status === 'mitigated' ? 'identified' : 'mitigated'
                })}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                {risk.status === 'mitigated' ? 'Reopen' : 'Mark Mitigated'}
              </button>
              <button
                onClick={() => onRiskDelete(risk.id)}
                className="text-sm text-red-600 hover:text-red-900"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}